import React from 'react';
import axios from 'axios';
import { createRoot } from 'react-dom/client';
import { CookiesProvider } from 'react-cookie';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import './index.css';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PrimeReactProvider } from 'primereact/api';
import reportWebVitals from './reportWebVitals';
import { store } from './store';
import App from './App';
import { addSessionHeaders } from './utils/sessionIds';
import { i18nextInstance } from './translations';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { SimeraDesignSystem } from './primereact';

const Auth0Domain: string = import.meta.env.REACT_APP_AUTH0_DOMAIN ?? '';
const Auth0ClientId: string = import.meta.env.REACT_APP_AUTH0_CLIENT_ID ?? '';
// if (process.env.NODE_ENV !== 'development') // temporaly enabled for all environments
Sentry.init({
  dsn:
    import.meta.env.REACT_APP_SENTRY_DSN ??
    'https://04a44d0c1668d12c6ea20ba01d93d8ff@o4506072464293888.ingest.us.sentry.io/4506072466653184',
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.reportingObserverIntegration(),
  ],
  environment: process.env.NODE_ENV,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: Number(import.meta.env.REACT_APP_SENTRY_SAMPLE_RATE) ?? 1.0,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [import.meta.env.REACT_APP_API_URL as string],
});

// Calls to the Simera API made with axios carry the device and session ids too (identity-and-funnel.md §8)
axios.interceptors.request.use(addSessionHeaders);

createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <Auth0Provider
      domain={Auth0Domain}
      clientId={Auth0ClientId}
      authorizationParams={{ redirect_uri: window.location.origin }}
      useRefreshTokens
    >
      <I18nextProvider i18n={i18nextInstance}>
        <Provider store={store}>
          <CookiesProvider>
            <PrimeReactProvider value={{ pt: SimeraDesignSystem }}>
              <App />
            </PrimeReactProvider>
          </CookiesProvider>
        </Provider>
      </I18nextProvider>
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
